import { FunctionComponent } from 'react';
import {
    Button,
    Card,
    CardContent,
    Grid,
    Stack,
    SvgIcon,
    TextField,
} from '@mui/material';
import { DateRangePicker } from '@/components/DatePicker/DateRangePicker';
import { useFormik } from 'formik';
import { DateRange } from '@/models/general';
import ArrowRightIcon from '@heroicons/react/24/solid/ArrowRightIcon';
import { AggregatedTicketSearchParams } from '@/models/aggregatedTicket';
import { LoadingButton } from '@mui/lab';

export const productionTypeOptions = [
    ['-', 'Alle'],
    ['BARCODE', 'eTicket'],
    ['PICKUP', 'Pick-up-Voucher'],
    ['KEYCARD', 'Keycard'],
    ['MOBILE_FLOW', 'Handyticket'],
    [
        'INTERNAL_PRODUCTION',
        'Keycard Produktion und Versand durch die Bergbahnen',
    ],
];

export const environmentOptions = [
    ['-', 'Alle'],
    ['portal', 'Portal (starjack.at)'],
    ['integration', 'Webshop'],
];

type StatisticFilterValues = {
    orderRange: DateRange;
    productionRange: DateRange;
    ticketStartRange: DateRange;
    cancelRange: DateRange;
    environment: string;
    productionEnvironment: string;
};

type StatisticsFilterProps = {
    onSearch: (
        params: Omit<AggregatedTicketSearchParams, 'statisticTypes'>
    ) => void;
    loading?: boolean;
};

export const StatisticsFilter: FunctionComponent<StatisticsFilterProps> = ({
    onSearch,
    loading,
}) => {
    const formik = useFormik<StatisticFilterValues>({
        initialValues: {
            // Add your initial form values here
            orderRange: [null, null],
            productionRange: [null, null],
            ticketStartRange: [null, null],
            cancelRange: [null, null],
            productionEnvironment: '-',
            environment: '-',
        },
        onSubmit: (values) => {
            const [orderDateFrom, orderDateTo] = values.orderRange;
            const [productionDateFrom, productionDateTo] =
                values.productionRange;
            const [ticketStartFrom, ticketStartTo] = values.ticketStartRange;
            const [cancellationDateStartFrom, cancellationDateStartTo] =
                values.cancelRange;

            const { environment, productionEnvironment } = values;

            onSearch({
                orderDateFrom,
                orderDateTo,
                productionDateFrom,
                productionDateTo,
                ticketStartFrom,
                ticketStartTo,
                cancellationDateStartFrom,
                cancellationDateStartTo,
                integrationRefSet:
                    environment === '-' ? null : environment === 'integration',
                identificationType:
                    productionEnvironment === '-'
                        ? null
                        : productionEnvironment,
            });
        },

        onReset: () => {
            onSearch({});
        },
    });
    const setPickerValue =
        (name: keyof StatisticFilterValues) => (value: unknown) => {
            formik.setFieldValue(name, value);
        };

    return (
        <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            <Card>
                <CardContent>
                    <Grid spacing={6} container>
                        <Grid
                            justifyContent='center'
                            alignItems='center'
                            xs={6}
                            item
                        >
                            <DateRangePicker
                                onChange={setPickerValue('orderRange')}
                                value={formik.values['orderRange']}
                                title='Bestellung'
                                fromText='von'
                                toText='bis'
                            />
                        </Grid>

                        <Grid xs={6} item>
                            <DateRangePicker
                                onChange={setPickerValue('productionRange')}
                                value={formik.values['productionRange']}
                                title='Produktion'
                                fromText='von'
                                toText='bis'
                            />
                        </Grid>

                        <Grid xs={6} item>
                            <DateRangePicker
                                onChange={setPickerValue('ticketStartRange')}
                                value={formik.values['ticketStartRange']}
                                title='Ticketstart'
                                fromText='von'
                                toText='bis'
                            />
                        </Grid>

                        <Grid xs={6} item>
                            <DateRangePicker
                                onChange={setPickerValue('cancelRange')}
                                value={formik.values['cancelRange']}
                                title='Stornierung'
                                fromText='von'
                                toText='bis'
                            />
                        </Grid>

                        <Grid xs={6} item>
                            <TextField
                                fullWidth
                                label='Produktionstyp'
                                required
                                select
                                value={formik.values['productionEnvironment']}
                                onChange={(e) => {
                                    const { value } = e.target;

                                    formik.setFieldValue(
                                        'productionEnvironment',
                                        value
                                    );
                                }}
                                SelectProps={{ native: true }}
                            >
                                {productionTypeOptions.map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid xs={6} item>
                            <TextField
                                fullWidth
                                label='Buchungsoberfläche'
                                value={formik.values['environment']}
                                onChange={(e) => {
                                    const { value } = e.target;

                                    formik.setFieldValue('environment', value);
                                }}
                                SelectProps={{ native: true }}
                                required
                                select
                            >
                                {environmentOptions.map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid display='flex' alignItems='center' xs={3} item>
                            <Stack direction='row' spacing={3}>
                                <LoadingButton
                                    loading={loading}
                                    type='submit'
                                    variant='contained'
                                >
                                    Suchen
                                    <SvgIcon
                                        sx={{
                                            ml: 2,
                                        }}
                                    >
                                        <ArrowRightIcon />
                                    </SvgIcon>
                                </LoadingButton>

                                {formik.dirty && (
                                    <Button
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                        type='reset'
                                        variant='text'
                                    >
                                        Zurücksetzen
                                    </Button>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </form>
    );
};
